<template>
  <UiContainer class="checkout-view">
    <h1 class="checkout-view__title">Оформление заказа</h1>

    <div v-if="isEmpty" class="checkout-view__state">
      <span class="checkout-view__state-icon">🛒</span>
      <h3 class="checkout-view__state-heading">Корзина пуста</h3>
      <p class="checkout-view__state-text">Добавьте товары в корзину для оформления заказа</p>
      <UiButton to="/catalog">Перейти к покупкам</UiButton>
    </div>

    <div v-else class="checkout-view__grid">
      <div class="checkout-view__form">
        <div class="checkout-view__steps">
          <button
            v-for="s in steps"
            :key="s.value"
            type="button"
            class="checkout-view__step"
            :class="{ 'checkout-view__step--active': currentStep === s.value }"
            @click="goToStep(s.value)"
          >
            {{ s.value }}. {{ s.title }}
          </button>
        </div>

        <UiCard v-if="currentStep === 1" class="checkout-view__card">
          <h2 class="checkout-view__card-title">Проверьте ваш заказ</h2>
          <div v-for="item in cartItems" :key="item.productId" class="checkout-view__row">
            <img
              :src="item.image || '/placeholder-product.svg'"
              :alt="item.name"
              class="checkout-view__row-img"
              width="60"
              height="60"
            />
            <div class="checkout-view__row-info">
              <h4 class="checkout-view__row-name">{{ item.name }}</h4>
              <p class="checkout-view__row-id">Артикул: {{ item.sku || item.productId }}</p>
            </div>
            <div class="checkout-view__row-meta">
              <span>{{ item.quantity }} шт.</span>
              <strong>{{
                formatPriceWithCents(item.totalPrice ?? item.price * item.quantity)
              }}</strong>
            </div>
          </div>
        </UiCard>

        <UiCard v-if="currentStep === 1 && hasSplitPreview" class="checkout-view__card">
          <h2 class="checkout-view__card-title">Разделение по поставщикам</h2>
          <div
            v-for="(supplierGroup, index) in splitPreview?.suppliers ?? []"
            :key="supplierGroup.supplier?.id ?? index"
            class="checkout-view__supplier"
          >
            <div class="checkout-view__supplier-head">
              <div>
                <strong>{{ supplierGroup.supplier?.name || 'Поставщик' }}</strong>
                <span class="checkout-view__supplier-count"
                  >{{ supplierGroup.items.length }} позиций</span
                >
              </div>
              <div class="checkout-view__supplier-totals">
                <span>НДС: {{ formatPriceWithCents(supplierGroup.tax_amount) }}</span>
                <strong>Итого: {{ formatPriceWithCents(supplierGroup.total_amount) }}</strong>
              </div>
            </div>
            <div class="checkout-view__table-wrap">
              <table class="checkout-view__table">
                <thead>
                  <tr>
                    <th>Товар</th>
                    <th>Кол-во</th>
                    <th>Цена с НДС</th>
                    <th>Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in supplierGroup.items" :key="item.product.id">
                    <td>
                      <div>{{ item.product.name }}</div>
                      <div class="checkout-view__table-sku">SKU: {{ item.product.sku }}</div>
                    </td>
                    <td>{{ item.quantity }}</td>
                    <td>{{ formatPriceWithCents(item.unit_price_with_tax) }}</td>
                    <td>
                      <strong>{{ formatPriceWithCents(item.total_price) }}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </UiCard>

        <UiCard v-if="currentStep === 2" class="checkout-view__card">
          <h2 class="checkout-view__card-title">Данные заказчика</h2>
          <div class="checkout-view__radios">
            <label class="checkout-view__radio">
              <input v-model="orderForm.customerType" type="radio" value="individual" />
              <span>Физическое лицо</span>
            </label>
            <label class="checkout-view__radio">
              <input v-model="orderForm.customerType" type="radio" value="company" />
              <span>Юридическое лицо</span>
            </label>
          </div>
          <div v-if="orderForm.customerType === 'individual'" class="checkout-view__field">
            <label class="checkout-view__label">ФИО</label>
            <UiInput v-model="orderForm.customerName" placeholder="Иванов Иван Иванович" required />
          </div>
          <div v-if="orderForm.customerType === 'company'" class="checkout-view__field">
            <label class="checkout-view__label">Название организации</label>
            <UiInput v-model="orderForm.customerName" placeholder="ООО Ромашка" required />
          </div>
          <div v-if="orderForm.customerType === 'company'" class="checkout-view__field">
            <label class="checkout-view__label">ИНН</label>
            <UiInput
              v-model="orderForm.customerInn"
              input-filter="digits"
              maxlength="12"
              placeholder="10 или 12 цифр"
              required
            />
          </div>
          <div class="checkout-view__field">
            <label class="checkout-view__label">Телефон</label>
            <UiInput
              v-model="orderForm.customerPhone"
              input-filter="phone"
              placeholder="+7 (999) 999-99-99"
              required
            />
          </div>
          <p v-if="customerStepError()" class="checkout-view__hint" role="status">
            Чтобы продолжить: {{ customerStepError() }}
          </p>
        </UiCard>

        <UiCard v-if="currentStep === 3" class="checkout-view__card">
          <h2 class="checkout-view__card-title">Способ доставки</h2>
          <div class="checkout-view__radios">
            <label class="checkout-view__radio">
              <input v-model="orderForm.deliveryMethod" type="radio" value="address" />
              <span>До адреса</span>
            </label>
            <label class="checkout-view__radio">
              <input v-model="orderForm.deliveryMethod" type="radio" value="terminal" />
              <span>До терминала</span>
            </label>
          </div>
          <div class="checkout-view__field">
            <label class="checkout-view__label">Город</label>
            <CityAutocomplete
              v-model="orderForm.deliveryCityName"
              :city-id="orderForm.deliveryCityId"
              @select="(city) => (orderForm.deliveryCityId = city?.id ?? null)"
            />
          </div>
          <template v-if="orderForm.deliveryMethod === 'address'">
            <div class="checkout-view__field">
              <label class="checkout-view__label">Улица</label>
              <UiInput v-model="orderForm.deliveryStreet" placeholder="ул. Ленина" required />
            </div>
            <div class="checkout-view__address-row">
              <div class="checkout-view__field checkout-view__field--house">
                <label class="checkout-view__label">Дом</label>
                <UiInput v-model="orderForm.deliveryHouse" placeholder="1" required />
              </div>
              <div class="checkout-view__field checkout-view__field--house">
                <label class="checkout-view__label">Кв./офис</label>
                <UiInput v-model="orderForm.deliveryApartment" placeholder="12" />
              </div>
              <div class="checkout-view__field checkout-view__field--postal">
                <label class="checkout-view__label">Индекс</label>
                <UiInput
                  v-model="orderForm.deliveryPostalCode"
                  input-filter="digits"
                  maxlength="6"
                  placeholder="620000"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <div class="checkout-view__field">
              <label class="checkout-view__label">Терминал</label>
              <TerminalSelect
                v-model="orderForm.deliveryTerminalId"
                :city-id="orderForm.deliveryCityId"
                @select="onTerminalSelect"
              />
            </div>
            <p v-if="deliveryQuoteLoading" class="checkout-view__delivery-note">
              Считаем стоимость доставки…
            </p>
            <p
              v-else-if="deliveryQuoteAmount !== null"
              class="checkout-view__quote"
              data-testid="delivery-quote"
            >
              Стоимость доставки до терминала:
              <strong>{{ formatPriceWithCents(deliveryQuoteAmount) }}</strong>
            </p>
          </template>
          <p
            v-if="deliveryStepTouched && deliveryStepError()"
            class="checkout-view__error"
            role="alert"
          >
            {{ deliveryStepError() }}
          </p>
          <p v-if="orderForm.deliveryMethod === 'address'" class="checkout-view__delivery-note">
            Стоимость доставки рассчитывается индивидуально менеджером по каждому заказу.
          </p>
        </UiCard>

        <UiCard v-if="currentStep === 4" class="checkout-view__card">
          <h2 class="checkout-view__card-title">Способ оплаты</h2>
          <!--
            Онлайн-оплата картой/СБП пока не подключена (нет платёжного шлюза) - см. комментарий
            у CheckoutOrderForm.paymentMethod в скрипте ниже. Единственный рабочий способ сейчас -
            оплата по счёту.
          -->
          <p class="checkout-view__delivery-note">
            Оплата по счёту: после оформления заказа менеджер выставит счёт на безналичную оплату.
          </p>
        </UiCard>

        <UiCard v-if="currentStep === 5" class="checkout-view__card">
          <h2 class="checkout-view__card-title">Дополнительная информация</h2>
          <div class="checkout-view__field">
            <label class="checkout-view__label">Комментарий к заказу (необязательно)</label>
            <UiTextarea
              v-model="orderForm.comment"
              placeholder="Укажите дополнительные пожелания по заказу..."
              :rows="3"
            />
          </div>
        </UiCard>

        <p
          v-if="currentStep === 1 && !isEmpty && !previewCoversCart"
          class="checkout-view__hint"
          role="status"
        >
          {{
            splitPreviewLoading
              ? 'Загружаем цены и поставщиков…'
              : 'Не удалось показать цены и поставщиков. Обновите страницу.'
          }}
        </p>
        <div class="checkout-view__nav">
          <UiButton v-if="currentStep > 1" variant="secondary" @click="currentStep--"
            >Назад</UiButton
          >
          <div v-else />
          <template v-if="currentStep < 5">
            <UiButton :disabled="currentStep !== 3 && !canProceedToNextStep" @click="nextStep"
              >Далее</UiButton
            >
          </template>
          <div v-else class="checkout-view__nav-actions">
            <!--
              В режиме продолжения черновика кнопки нет: обновления черновика на бэкенде
              пока не существует, а createDraft() создал бы второй черновик и очистил
              корзину. Изменения сохраняются отправкой заказа.
            -->
            <UiButton
              v-if="!isResumingDraft"
              variant="secondary"
              :loading="orderLoading"
              :disabled="!canProceedToNextStep"
              @click="saveDraft"
            >
              Сохранить черновик
            </UiButton>
            <UiButton :loading="orderLoading" :disabled="!canSubmitOrder" @click="submitOrder">
              Отправить заказ
            </UiButton>
          </div>
        </div>
      </div>

      <UiCard class="checkout-view__summary checkout-view__summary--sticky">
        <h2 class="checkout-view__card-title">Сводка заказа</h2>
        <div v-if="user?.company" class="checkout-view__company">
          <h4 class="checkout-view__company-title">Заказчик</h4>
          <p>{{ user.company.name }}</p>
          <p v-if="user.company.inn">ИНН: {{ user.company.inn }}</p>
          <p>{{ user.name }}</p>
        </div>
        <div class="checkout-view__summary-row">
          <span>Товаров:</span>
          <span>{{ itemsCount }}</span>
        </div>
        <div class="checkout-view__summary-row">
          <span>Сумма товаров:</span>
          <span>{{ formatPriceWithCents(totalAmount) }}</span>
        </div>
        <PriceBreakdown
          :total-amount="totalAmount"
          :tax-amount="taxAmount"
          :delivery-amount="deliveryQuoteAmount ?? 0"
          :delivery-label="deliveryQuoteLabel"
        />
        <div v-if="currentStep > 1" class="checkout-view__details">
          <h4 class="checkout-view__details-title">Детали заказа</h4>
          <p><strong>Доставка:</strong> {{ getDeliveryMethodText() }}</p>
          <p v-if="orderForm.deliveryCityName">
            <strong>Город:</strong> {{ orderForm.deliveryCityName }}
          </p>
          <p v-if="orderForm.deliveryMethod === 'terminal' && orderForm.deliveryTerminalName">
            <strong>Терминал:</strong> {{ orderForm.deliveryTerminalName }}
          </p>
          <p v-if="orderForm.deliveryMethod === 'address' && orderForm.deliveryStreet">
            <strong>Адрес:</strong> {{ formattedDeliveryAddress }}
          </p>
          <p v-if="currentStep > 2"><strong>Оплата:</strong> {{ getPaymentMethodText() }}</p>
        </div>
      </UiCard>
    </div>
  </UiContainer>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import apiClient from '@/api/client'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import UiInput from '@/shared/ui/UiInput.vue'
import UiTextarea from '@/shared/ui/UiTextarea.vue'
import CityAutocomplete from '@/modules/order/components/CityAutocomplete.vue'
import TerminalSelect from '@/modules/order/components/TerminalSelect.vue'
import {
  deliveryApi,
  quotePriceToRubles,
  type DeliveryQuote,
  type CarrierTerminalOption,
} from '@/modules/order/api/delivery.api'
import { PriceBreakdown } from '@/modules/price/public'
import { useCartWorkspace, useCheckoutOrders, type CartItem } from '@/modules/order/public'
import { useAuthSession } from '@/modules/auth/public'
import { useAppNotifications } from '@/modules/common/public'
import { cartApi, type CartSplitResponse } from '@/modules/order/api/cart.api'
import {
  isDraftSubmittable,
  queryWithoutDraftId,
  resolveResumedDraftId,
  type DraftResumeOutcome,
} from '@/modules/order/model/draftResume'
import { formatPriceWithCents } from '@/utils/price'
import { clearIdempotencyAttempt, idempotencyKeyForAttempt } from '@/utils/idempotency'

const router = useRouter()
const route = useRoute()
const cartStore = useCartWorkspace()
const orderStore = useCheckoutOrders()
const authSession = useAuthSession()
const notificationStore = useAppNotifications()

interface CheckoutOrderForm {
  comment: string
  deliveryMethod: 'address' | 'terminal'
  deliveryCityName: string
  deliveryCityId: number | null
  deliveryStreet: string
  deliveryHouse: string
  deliveryApartment: string
  deliveryPostalCode: string
  deliveryTerminalId: string | null
  deliveryTerminalName: string
  // Единственное значение сейчас - 'bank_transfer' (оплата по счёту, отмечается вручную
  // админом). 'card'/'credit' были в UI, но платёжный шлюз не подключён - убраны из выбора,
  // пока не появится реальная онлайн-оплата (см. Forge: Projects/prolab/Decisions/payment-gateway.md,
  // фаза 6). Тип оставлен объединением на будущее, чтобы не переписывать его при возврате шлюза.
  paymentMethod: 'card' | 'bank_transfer' | 'credit'
  customerType: 'individual' | 'company'
  customerName: string
  customerInn: string
  customerPhone: string
}

const currentStep = ref(1)
const maxStepReached = ref(1)
const deliveryStepTouched = ref(false)
const orderForm = ref<CheckoutOrderForm>({
  comment: '',
  deliveryMethod: 'address',
  deliveryCityName: '',
  deliveryCityId: null,
  deliveryStreet: '',
  deliveryHouse: '',
  deliveryApartment: '',
  deliveryPostalCode: '',
  deliveryTerminalId: null,
  deliveryTerminalName: '',
  paymentMethod: 'bank_transfer',
  customerType: 'individual',
  customerName: '',
  customerInn: '',
  customerPhone: '',
})

const formattedDeliveryAddress = computed(() => {
  const form = orderForm.value
  return [
    form.deliveryStreet,
    form.deliveryHouse && `д. ${form.deliveryHouse}`,
    form.deliveryApartment && `кв./оф. ${form.deliveryApartment}`,
  ]
    .filter(Boolean)
    .join(', ')
})

// Расчёт стоимости доставки до терминала: живой тариф провайдера, а не «у менеджера».
// Ручной расчёт (is_manual) и любая ошибка схлопываются в null — показываем заглушку.
const deliveryQuote = ref<DeliveryQuote | null>(null)
const deliveryQuoteLoading = ref(false)
let deliveryQuoteRequestId = 0

const deliveryQuoteAmount = computed(() => {
  if (orderForm.value.deliveryMethod !== 'terminal') return null
  if (!deliveryQuote.value || deliveryQuote.value.is_manual) return null
  return quotePriceToRubles(deliveryQuote.value.price)
})

const deliveryQuoteLabel = computed(() => {
  if (orderForm.value.deliveryMethod === 'terminal') {
    return deliveryQuoteAmount.value !== null ? 'До терминала' : 'Рассчитывается менеджером'
  }
  return 'Рассчитывается индивидуально'
})

async function refreshDeliveryQuote(): Promise<void> {
  // Ответы приходят не по порядку: актуален только последний запрос
  const request_id = ++deliveryQuoteRequestId
  const city_id = orderForm.value.deliveryCityId
  if (orderForm.value.deliveryMethod !== 'terminal' || city_id === null) {
    deliveryQuote.value = null
    deliveryQuoteLoading.value = false
    return
  }

  deliveryQuoteLoading.value = true
  try {
    const quote = await deliveryApi.getQuote(city_id, 'terminal')
    if (request_id === deliveryQuoteRequestId) deliveryQuote.value = quote
  } catch (e) {
    if (request_id !== deliveryQuoteRequestId) return
    console.error('Ошибка расчёта стоимости доставки', e)
    deliveryQuote.value = null
  } finally {
    if (request_id === deliveryQuoteRequestId) deliveryQuoteLoading.value = false
  }
}

watch(() => [orderForm.value.deliveryMethod, orderForm.value.deliveryCityId], refreshDeliveryQuote)

function onTerminalSelect(terminal: CarrierTerminalOption | null): void {
  orderForm.value.deliveryTerminalName = terminal?.name ?? ''
}

const steps = [
  { title: 'Товары', value: 1 },
  { title: 'Заказчик', value: 2 },
  { title: 'Доставка', value: 3 },
  { title: 'Оплата', value: 4 },
  { title: 'Подтверждение', value: 5 },
]

const user = computed(() => authSession.user.value)
const cartItems = computed(() => cartStore.items.value)
const isEmpty = computed(() => cartStore.isEmpty.value)
const itemsCount = computed(() => cartStore.itemsCount.value)
const totalAmount = computed(() => {
  const v = cartStore.totalAmount.value
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string') {
    const p = parseFloat(v)
    return Number.isNaN(p) ? 0 : p
  }
  return 0
})
const taxAmount = computed(() => {
  const v = cartStore.taxAmount.value
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string') {
    const p = parseFloat(v)
    return Number.isNaN(p) ? 0 : p
  }
  return 0
})
const orderLoading = computed(() => orderStore.loading.value)

const splitPreview = ref<CartSplitResponse | null>(null)
const splitPreviewLoading = ref(false)
const hasSplitPreview = computed(() => (splitPreview.value?.suppliers.length ?? 0) > 0)

const canProceedToNextStep = computed(() => {
  switch (currentStep.value) {
    case 1:
      return !isEmpty.value && previewCoversCart.value
    case 2:
      return isCustomerStepValid()
    case 3:
      return isDeliveryStepValid()
    case 4:
      return !!orderForm.value.paymentMethod
    default:
      return true
  }
})
const canSubmitOrder = computed(
  () =>
    isCustomerStepValid() &&
    isDeliveryStepValid() &&
    !!orderForm.value.paymentMethod &&
    !orderLoading.value,
)

/** Черновик после успешного restore; не брать из URL draftId */
const activeDraftId = ref<string | null>(null)
const draftResumeOutcome = ref<DraftResumeOutcome | null>(null)
// Один ключ на попытку; sessionStorage переживает reload
const CHECKOUT_ATTEMPT_SCOPE = 'checkout'
const isResumingDraft = computed(
  () => activeDraftId.value !== null && isDraftSubmittable(draftResumeOutcome.value),
)

function getDeliveryMethodText(): string {
  const m: Record<string, string> = { address: 'До адреса', terminal: 'До терминала' }
  return m[orderForm.value.deliveryMethod] ?? orderForm.value.deliveryMethod
}

function getPaymentMethodText(): string {
  const m: Record<string, string> = {
    card: 'Банковская карта',
    bank_transfer: 'Оплата по счёту',
    credit: 'В кредит',
  }
  return m[orderForm.value.paymentMethod] ?? orderForm.value.paymentMethod
}

const CHECKOUT_TERMS_CHANGED_CODE = 'checkout_terms_changed'

type ShownTerms = { supplierId: number; unitPriceWithTax: number; quantity: number }

/** Условия позиций из split-preview, которые сейчас отображаются на шаге «Товары». */
const shownTermsByProduct = computed(() => {
  const shown = new Map<string, ShownTerms>()
  for (const group of splitPreview.value?.suppliers ?? []) {
    const supplierId = group.supplier?.id
    if (typeof supplierId !== 'number') continue
    for (const item of group.items) {
      const amount = item.unit_price_with_tax?.amount
      if (typeof amount === 'number') {
        shown.set(String(item.product.id), {
          supplierId,
          unitPriceWithTax: amount,
          quantity: Number(item.quantity),
        })
      }
    }
  }
  return shown
})

/** Загруженное разбиение показывает каждую позицию корзины с тем же количеством. */
const previewCoversCart = computed(
  () =>
    !splitPreviewLoading.value &&
    cartItems.value.length > 0 &&
    cartItems.value.every(
      (item) => shownTermsByProduct.value.get(String(item.productId))?.quantity === item.quantity,
    ),
)

/**
 * Условия, с которыми покупатель ушёл с шага «Товары». Отправляются именно они, а не
 * текущий preview: он может обновиться позже, уже на другом шаге.
 */
const confirmedTerms = ref<Map<string, ShownTerms> | null>(null)

function confirmShownTerms(): boolean {
  if (!previewCoversCart.value) return false
  confirmedTerms.value = new Map(shownTermsByProduct.value)
  return true
}

/**
 * Позиции заказа с подтверждёнными на шаге «Товары» условиями. Backend не создаст заказ,
 * если фактические условия другие. `null` - для позиции нет подтверждённых условий или
 * её количество изменилось после подтверждения: отправлять нельзя.
 */
function itemsWithConfirmedTerms(): Array<{
  productId: string
  quantity: number
  confirmedSupplierId: number
  confirmedUnitPriceWithTax: number
}> | null {
  const confirmed = confirmedTerms.value
  if (confirmed === null) return null

  const result = []
  for (const item of orderStore.formatOrderItems(cartItems.value)) {
    const terms = confirmed.get(String(item.productId))
    if (terms === undefined || terms.quantity !== item.quantity) return null
    result.push({
      ...item,
      confirmedSupplierId: terms.supplierId,
      confirmedUnitPriceWithTax: terms.unitPriceWithTax,
    })
  }
  return result
}

/** Показать актуальные условия: перечитать корзину и разбиение, вернуть на шаг «Товары». */
async function refreshTermsForReview(message: string | undefined) {
  notificationStore.showWarning(message || 'Условия заказа изменились. Проверьте заказ.')
  try {
    await cartStore.initCart()
  } catch (e) {
    console.error('Ошибка обновления корзины', e)
  }
  confirmedTerms.value = null
  await loadSplitPreview()
  currentStep.value = 1
}

// Ответы приходят в любом порядке: применяется только ответ на последний запрос
let splitPreviewRequest = 0

async function loadSplitPreview() {
  if (!authSession.isAuthenticated.value) return
  const request = ++splitPreviewRequest
  splitPreviewLoading.value = true
  try {
    const preview = await cartApi.getSplitPreview()
    if (request === splitPreviewRequest) splitPreview.value = preview
  } catch (e) {
    console.error('Ошибка загрузки разделения заказа', e)
  } finally {
    if (request === splitPreviewRequest) splitPreviewLoading.value = false
  }
}

function goToStep(step: number) {
  if (step > maxStepReached.value) return
  if (currentStep.value === 1 && step > 1 && !confirmShownTerms()) return

  if (step > currentStep.value && currentStep.value === 3 && !isDeliveryStepValid()) {
    deliveryStepTouched.value = true
    return
  }

  currentStep.value = step
}

function nextStep() {
  if (currentStep.value === 3 && !isDeliveryStepValid()) {
    deliveryStepTouched.value = true
    return
  }

  if (canProceedToNextStep.value && currentStep.value < 5) {
    if (currentStep.value === 1 && !confirmShownTerms()) return
    currentStep.value++
    if (currentStep.value > maxStepReached.value) maxStepReached.value = currentStep.value
  }
}

async function submitOrder() {
  if (!canSubmitOrder.value) return
  try {
    const orderData: {
      items: Array<{
        productId: string
        quantity: number
        confirmedSupplierId?: number
        confirmedUnitPriceWithTax?: number
      }>
      deliveryMethod: 'address' | 'terminal'
      paymentMethod: 'card' | 'bank_transfer' | 'credit'
      comment?: string
      cityId?: number
      deliveryStreet?: string
      deliveryHouse?: string
      deliveryApartment?: string
      deliveryPostalCode?: string
      terminalId?: string
      customerType?: 'individual' | 'company'
      customerName?: string
      customerInn?: string
      customerPhone?: string
    } = {
      items: orderStore.formatOrderItems(cartItems.value),
      deliveryMethod: orderForm.value.deliveryMethod,
      paymentMethod: orderForm.value.paymentMethod,
      customerType: orderForm.value.customerType,
      customerName: orderForm.value.customerName,
      customerPhone: orderForm.value.customerPhone,
    }
    if (orderForm.value.comment) {
      orderData['comment'] = orderForm.value.comment
    }
    if (orderForm.value.deliveryCityId) {
      orderData['cityId'] = orderForm.value.deliveryCityId
    }
    if (orderForm.value.deliveryMethod === 'terminal' && orderForm.value.deliveryTerminalId) {
      orderData['terminalId'] = orderForm.value.deliveryTerminalId
    }
    if (orderForm.value.deliveryMethod === 'address' && orderForm.value.deliveryStreet) {
      orderData['deliveryStreet'] = orderForm.value.deliveryStreet
      if (orderForm.value.deliveryHouse) orderData['deliveryHouse'] = orderForm.value.deliveryHouse
      if (orderForm.value.deliveryApartment)
        orderData['deliveryApartment'] = orderForm.value.deliveryApartment
      if (orderForm.value.deliveryPostalCode)
        orderData['deliveryPostalCode'] = orderForm.value.deliveryPostalCode
    }
    if (orderForm.value.customerType === 'company' && orderForm.value.customerInn) {
      orderData['customerInn'] = orderForm.value.customerInn
    }
    // Ветка выбирается по состоянию восстановления, а не по URL: submitDraft не передаёт
    // позиции, backend берёт их из черновика. Если восстановление сорвалось, на экране
    // прежняя корзина - её и надо отправлять обычным createOrder.
    const draft_id = activeDraftId.value
    let result
    if (draft_id !== null) {
      const delivery: {
        cityId?: number
        deliveryStreet?: string
        deliveryHouse?: string
        deliveryApartment?: string
        deliveryPostalCode?: string
        terminalId?: string
      } = {}
      if (orderForm.value.deliveryCityId) delivery.cityId = orderForm.value.deliveryCityId
      if (orderForm.value.deliveryTerminalId)
        delivery.terminalId = orderForm.value.deliveryTerminalId
      if (orderForm.value.deliveryStreet) delivery.deliveryStreet = orderForm.value.deliveryStreet
      if (orderForm.value.deliveryHouse) delivery.deliveryHouse = orderForm.value.deliveryHouse
      if (orderForm.value.deliveryApartment)
        delivery.deliveryApartment = orderForm.value.deliveryApartment
      if (orderForm.value.deliveryPostalCode)
        delivery.deliveryPostalCode = orderForm.value.deliveryPostalCode
      result = await orderStore.submitDraft(draft_id, delivery)
    } else {
      const confirmedItems = itemsWithConfirmedTerms()
      if (confirmedItems === null) {
        await refreshTermsForReview('Проверьте поставщика и цены в заказе перед отправкой.')
        return
      }
      orderData.items = confirmedItems
      const idempotencyKey = idempotencyKeyForAttempt(
        CHECKOUT_ATTEMPT_SCOPE,
        JSON.stringify(orderData),
      )
      result = await orderStore.createOrder(orderData, idempotencyKey)
      if (!result.success && 'code' in result && result.code === CHECKOUT_TERMS_CHANGED_CODE) {
        await refreshTermsForReview(result.error)
        return
      }
    }
    if (result.success) {
      clearIdempotencyAttempt(CHECKOUT_ATTEMPT_SCOPE)
      await cartStore.clearCart()
      notificationStore.showSuccess('Ваша заявка оформлена, ожидайте подтверждения.')
      router.push('/orders')
    } else {
      notificationStore.showError(result.error || 'Ошибка при оформлении заказа')
    }
  } catch (e) {
    console.error('Ошибка при оформлении заказа', e)
    notificationStore.showError('Произошла ошибка при оформлении заказа')
  }
}

async function saveDraft() {
  if (!authSession.isAuthenticated.value) {
    notificationStore.showWarning('Для сохранения черновика необходимо войти в систему')
    return
  }

  // Страховка на случай вызова в обход скрытой кнопки: createDraft() здесь создал бы
  // дубликат вместо обновления открытого черновика.
  if (isResumingDraft.value) {
    notificationStore.showWarning(
      'Черновик уже сохранён. Изменения применятся при отправке заказа.',
    )
    return
  }
  try {
    const orderData: {
      items: Array<{ productId: string; quantity: number }>
      deliveryMethod: 'address' | 'terminal'
      paymentMethod: 'card' | 'bank_transfer' | 'credit'
      comment?: string
      cityId?: number
      deliveryStreet?: string
      deliveryHouse?: string
      deliveryApartment?: string
      deliveryPostalCode?: string
      terminalId?: string
      customerType?: 'individual' | 'company'
      customerName?: string
      customerInn?: string
      customerPhone?: string
    } = {
      items: orderStore.formatOrderItems(cartItems.value),
      deliveryMethod: orderForm.value.deliveryMethod,
      paymentMethod: orderForm.value.paymentMethod,
      customerType: orderForm.value.customerType,
      customerName: orderForm.value.customerName,
      customerPhone: orderForm.value.customerPhone,
    }
    if (orderForm.value.comment) {
      orderData['comment'] = orderForm.value.comment
    }
    if (orderForm.value.deliveryCityId) {
      orderData['cityId'] = orderForm.value.deliveryCityId
    }
    if (orderForm.value.deliveryMethod === 'terminal' && orderForm.value.deliveryTerminalId) {
      orderData['terminalId'] = orderForm.value.deliveryTerminalId
    }
    if (orderForm.value.deliveryMethod === 'address' && orderForm.value.deliveryStreet) {
      orderData['deliveryStreet'] = orderForm.value.deliveryStreet
      if (orderForm.value.deliveryHouse) orderData['deliveryHouse'] = orderForm.value.deliveryHouse
      if (orderForm.value.deliveryApartment)
        orderData['deliveryApartment'] = orderForm.value.deliveryApartment
      if (orderForm.value.deliveryPostalCode)
        orderData['deliveryPostalCode'] = orderForm.value.deliveryPostalCode
    }
    if (orderForm.value.customerType === 'company' && orderForm.value.customerInn) {
      orderData['customerInn'] = orderForm.value.customerInn
    }
    const result = await orderStore.createDraft(orderData)
    if (result.success) {
      await cartStore.clearCart()
      notificationStore.showSuccess('Черновик заказа сохранен')
      const draftId = result.order?.id
      await router.push(draftId ? `/orders/${draftId}` : '/orders')
    } else {
      notificationStore.showError(result.error || 'Ошибка при сохранении черновика')
    }
  } catch (e) {
    console.error('Ошибка при сохранении черновика', e)
    notificationStore.showError('Произошла ошибка при сохранении черновика')
  }
}

/**
 * Выйти из режима продолжения черновика.
 *
 * `draftId` убирается из адреса, иначе URL продолжает обещать режим, в котором checkout
 * уже не находится: перезагрузка страницы снова полезла бы восстанавливать черновик, а
 * ссылка выглядела бы так, будто отправится он, а не показанная корзина.
 */
async function leaveDraftResume(outcome: DraftResumeOutcome): Promise<void> {
  activeDraftId.value = null
  draftResumeOutcome.value = outcome

  if (resolveResumedDraftId(route.query) === null) {
    return
  }

  await router.replace({ path: route.path, query: queryWithoutDraftId(route.query) })
}

async function restoreDraft(draft_id: string): Promise<void> {
  try {
    const response = await apiClient.get(`/api/orders/${draft_id}`)
    const draft = response.data?.data

    if (!draft || draft.status !== 'draft' || !Array.isArray(draft.items)) {
      notificationStore.showError('Черновик недоступен для продолжения')
      await router.replace('/orders')
      return
    }

    const items = draft.items
      .map((item: Record<string, unknown>): CartItem | null => {
        const raw_product = item['product']
        const product =
          raw_product && typeof raw_product === 'object' && !Array.isArray(raw_product)
            ? (raw_product as Record<string, unknown>)
            : {}
        const product_id = product['id'] ?? item['product_id']
        const quantity = Number(item['quantity'] ?? 0)
        if (product_id === undefined || !Number.isInteger(quantity) || quantity <= 0) return null

        const price_data = item['price_with_tax'] ?? item['price']
        const price_minor_units =
          price_data && typeof price_data === 'object' && !Array.isArray(price_data)
            ? Number((price_data as Record<string, unknown>)['amount'] ?? 0)
            : Number(price_data ?? 0)

        return {
          productId: String(product_id),
          name: typeof product['name'] === 'string' ? product['name'] : 'Товар',
          sku: typeof product['sku'] === 'string' ? product['sku'] : null,
          price: price_minor_units / 100,
          quantity,
        }
      })
      .filter((item: CartItem | null): item is CartItem => item !== null)

    if (items.length === 0) {
      notificationStore.showError('В черновике нет доступных товаров')
      await leaveDraftResume('unavailable')
      return
    }

    // Замена непустой корзины - потеря данных, поэтому только с явного согласия.
    if (cartItems.value.length > 0) {
      const confirmed = window.confirm(
        `В корзине уже есть товары (${cartItems.value.length}). ` +
          'Продолжение черновика заменит её содержимое позициями черновика. Продолжить?',
      )
      if (!confirmed) {
        // Ни одного обращения к корзине ещё не было: и клиентская, и серверная остаются как есть.
        notificationStore.showInfo('Черновик не открыт, корзина оставлена без изменений')
        await leaveDraftResume('declined')
        return
      }
    }

    const restore = await cartStore.restoreItems(items)
    if (restore.outcome !== 'replaced') {
      // Про «корзина не изменилась» можно говорить только когда откат действительно прошёл.
      notificationStore.showError(
        restore.outcome === 'rolled_back'
          ? 'Не удалось восстановить товары из черновика, корзина оставлена без изменений'
          : 'Не удалось восстановить товары из черновика, и вернуть прежнюю корзину не получилось. Проверьте содержимое корзины.',
      )
      await leaveDraftResume(restore.outcome)

      return
    }

    activeDraftId.value = draft_id
    draftResumeOutcome.value = 'replaced'

    orderForm.value.deliveryMethod = draft.delivery_method === 'terminal' ? 'terminal' : 'address'
    orderForm.value.deliveryCityId = Number.isInteger(Number(draft.city_id))
      ? Number(draft.city_id)
      : null
    orderForm.value.deliveryTerminalId =
      typeof draft.delivery_terminal_id === 'string' ? draft.delivery_terminal_id : null
    orderForm.value.deliveryTerminalName =
      typeof draft.delivery_terminal_name === 'string' ? draft.delivery_terminal_name : ''
    orderForm.value.deliveryCityName = typeof draft.city_name === 'string' ? draft.city_name : ''
    orderForm.value.deliveryStreet =
      typeof draft.delivery_street === 'string' ? draft.delivery_street : ''
    orderForm.value.deliveryHouse =
      typeof draft.delivery_house === 'string' ? draft.delivery_house : ''
    orderForm.value.deliveryApartment =
      typeof draft.delivery_apartment === 'string' ? draft.delivery_apartment : ''
    orderForm.value.deliveryPostalCode =
      typeof draft.delivery_postal_code === 'string' ? draft.delivery_postal_code : ''
    orderForm.value.comment = typeof draft.notes === 'string' ? draft.notes : ''

    currentStep.value = 5
    maxStepReached.value = 5
  } catch (error) {
    console.error('Ошибка восстановления черновика:', error)
    notificationStore.showError('Не удалось открыть черновик в оформлении')
    await router.replace('/orders')
  }
}

onMounted(async () => {
  if (!authSession.isAuthenticated.value)
    notificationStore.showWarning('Для оформления заказа необходимо войти в систему')
  else {
    await cartStore.initCart()
    const draft_id = route.query['draftId']
    if (typeof draft_id === 'string' && draft_id.trim() !== '') {
      await restoreDraft(draft_id)
    }
    if (isEmpty.value)
      notificationStore.showWarning('Добавьте товары в корзину для оформления заказа')
  }

  const company = user.value?.company
  if (company) {
    orderForm.value.customerType = 'company'
    orderForm.value.customerName = company.name ?? orderForm.value.customerName
    orderForm.value.customerInn = company.inn ?? orderForm.value.customerInn
    orderForm.value.customerPhone =
      company.phone ?? user.value?.phone ?? orderForm.value.customerPhone
  }
})

watch(
  () => cartItems.value.map((i) => `${i.productId}:${i.quantity}`).join('|'),
  () => {
    if (!isEmpty.value) loadSplitPreview()
  },
  { immediate: true },
)

function customerStepError(): string {
  const form = orderForm.value
  const phone_digits = form.customerPhone.replace(/\D/g, '')
  const inn_digits = form.customerInn.replace(/\D/g, '')

  if (!form.customerType) return 'Выберите тип заказчика.'
  if (!form.customerName.trim()) {
    return form.customerType === 'company' ? 'Укажите название организации.' : 'Укажите ФИО.'
  }
  if (form.customerType === 'company' && inn_digits.length !== 10 && inn_digits.length !== 12) {
    return 'ИНН должен содержать 10 или 12 цифр.'
  }
  if (phone_digits.length < 10 || phone_digits.length > 15) {
    return 'Укажите телефон для связи, например +7 (999) 999-99-99.'
  }
  return ''
}

function isCustomerStepValid(): boolean {
  return customerStepError() === ''
}

function deliveryStepError(): string {
  const form = orderForm.value
  if (!form.deliveryMethod) return 'Выберите способ доставки.'
  if (!form.deliveryCityId) return 'Выберите город доставки из списка.'
  if (form.deliveryMethod === 'terminal') {
    return form.deliveryTerminalId ? '' : 'Выберите терминал доставки.'
  }

  const street = form.deliveryStreet.trim()
  if (street === '') return 'Укажите улицу.'
  if (/^\d+$/.test(street)) return 'Укажите название улицы, а не только номер.'
  if (form.deliveryHouse.trim() === '') return 'Укажите номер дома.'
  return ''
}

function isDeliveryStepValid(): boolean {
  return deliveryStepError() === ''
}
</script>

<style scoped>
.checkout-view {
  padding-top: 24px;
  padding-bottom: 48px;
}

.checkout-view__title {
  margin: 0 0 24px;
  font-size: 24px;
  font-weight: 600;
}

.checkout-view__state {
  text-align: center;
  padding: 48px 16px;
}

.checkout-view__state-icon {
  font-size: 64px;
  display: block;
  margin-bottom: 16px;
  opacity: 0.6;
}

.checkout-view__state-heading {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
}

.checkout-view__state-text {
  margin: 0 0 24px;
  color: #586578;
}

.checkout-view__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
  align-items: start;
}

@media (max-width: 960px) {
  .checkout-view__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.checkout-view__steps {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.checkout-view__step {
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 6px;
  font: inherit;
  cursor: pointer;
  color: #586578;
}

.checkout-view__step--active {
  border-color: var(--color-accent);
  background: var(--color-accent);
  color: #fff;
}

.checkout-view__card {
  padding: 20px;
  margin-bottom: 24px;
}

.checkout-view__card-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
}

.checkout-view__row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}

.checkout-view__row-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  background: #f1f5f9;
}

.checkout-view__row-info {
  flex: 1;
  min-width: 0;
}

.checkout-view__row-name {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
}

.checkout-view__row-id {
  margin: 0;
  font-size: 12px;
  color: #586578;
}

.checkout-view__row-meta {
  text-align: right;
}

.checkout-view__row-meta strong {
  display: block;
}

.checkout-view__supplier {
  margin-bottom: 24px;
}

.checkout-view__supplier:last-child {
  margin-bottom: 0;
}

.checkout-view__supplier-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.checkout-view__supplier-count {
  display: block;
  font-size: 12px;
  color: #586578;
}

.checkout-view__supplier-totals {
  text-align: right;
}

.checkout-view__supplier-totals strong {
  display: block;
}

.checkout-view__table-wrap {
  overflow-x: auto;
}

.checkout-view__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.checkout-view__table th,
.checkout-view__table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.checkout-view__table th {
  font-weight: 600;
  color: #586578;
}

.checkout-view__table-sku {
  font-size: 12px;
  color: #586578;
}

.checkout-view__radios {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.checkout-view__radio {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font: inherit;
}

.checkout-view__radio input {
  width: 18px;
  height: 18px;
  accent-color: var(--color-accent);
}

.checkout-view__field {
  margin-top: 16px;
}

.checkout-view__address-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

@media (max-width: 600px) {
  .checkout-view__address-row {
    grid-template-columns: minmax(0, 1fr);
  }
}

.checkout-view__field--house,
.checkout-view__field--postal {
  margin-top: 16px;
}

.checkout-view__label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
}

.checkout-view__hint {
  margin: 12px 0 0;
  color: #586578;
  font-size: 14px;
}

.checkout-view__error {
  margin: 12px 0 0;
  color: #b91c1c;
  font-size: 14px;
}

.checkout-view__quote {
  margin: 12px 0 0;
  padding: 10px 12px;
  background: #f0f6ff;
  border-radius: 8px;
  font-size: 14px;
}

.checkout-view__address-picker {
  margin-bottom: 8px;
}

.checkout-view__address-selected {
  margin: 8px 0 0;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #334155;
}

.checkout-view__nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 24px;
}

.checkout-view__nav-actions {
  display: flex;
  gap: 12px;
}

.checkout-view__summary {
  padding: 20px;
}

.checkout-view__summary--sticky {
  position: sticky;
  top: 100px;
}

.checkout-view__company {
  padding: 12px;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 16px;
}

.checkout-view__company-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
}

.checkout-view__company p {
  margin: 0 0 4px;
  font-size: 14px;
}

.checkout-view__summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.checkout-view__details {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.checkout-view__details-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
}

.checkout-view__details p {
  margin: 0 0 4px;
  font-size: 14px;
}
</style>
